<template>
  <div>
    <div class="lg:block" >
      <div
        class="m-auto max-w-[68.5714rem] xl:max-w-[82.2857rem] 2xl:max-w-[96rem] h-[3.5714rem]
               flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
      >
        <div class="px-[1.4286rem] py-[1rem] md:flex">
          <span class="text-ash-100" role="status" aria-live="polite" aria-atomic="true">{{ start }}–{{ end }} of {{ totalResults }} results</span>
          <div v-if="shouldShowClearAll" class="underline ml-[0.7143rem] text-ash-500 font-normal hover:text-gold-300 cursor-pointer"
            aria-label="Clear all filters" role="button" @click="onClearAll" tabindex="0" @keydown.enter="onClearAll" @keydown.space.prevent="onClearAll">Clear All</div>
            <div  class="sr-only" role="status" aria-live="polite" aria-atomic="false">{{ liveMessage }}</div>
        </div>
        <Sort v-model="selectedSort" :options="PRODUCT_SORT_OPTIONS" @sort-change="handleSortChange" />
      </div>
    </div>
    <hr class="w-full border-t border-gray-300" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'
import { useRoute } from 'vue-router'
import Sort from '../../organism/product/sort.vue'
import type { SortOption } from '~~/shared/types/common'
import { PRODUCT_SORT_OPTIONS } from '~/components/utils/sortOptions'
import { URL_CONFIG } from '~~/shared/utils/url.const'

const route = useRoute()
const liveMessage = ref('')

const props = withDefaults(
  defineProps<{
    start: number
    end: number
    totalResults: number
    selectedFilters: Record<string, string[]>
    sortParams: { sort: string } | null
    clearAllFacets: () => void
    setSortParams: (sort: string) => void
    options?: SortOption[]
    showClearAll?: boolean
  }>(),
  {
    start: 0,
    end: 0,
    totalResults: 0,
    options: () => PRODUCT_SORT_OPTIONS
  }
)

const isBeyondBaseProducts = computed(() => {
  // NOSONAR: Safe regex, only strips trailing slashes
  const path = route.path.replace(/\/+$/, "")
  return path !== URL_CONFIG.PRODUCT_BASE_URL
})

const hasFacetQuery = computed(() => {
  const keys = Object.keys(route.query)
  return keys.some(k => k.toLowerCase().startsWith("f["))
})

const shouldShowClearAll = computed(() => {
  return isBeyondBaseProducts.value || hasFacetQuery.value
})

const selectedSort = ref(props.options[0]!.value)

const syncSelectedFromStore = () => {
  if (!props.sortParams) {
    selectedSort.value = props.options[0]!.value
    return
  }
  const opt = props.options.find(option => option.sortp === props.sortParams!.sort)
  if (opt) selectedSort.value = opt.value
}

onMounted(() => {
  syncSelectedFromStore()
})

watch(
  () => props.sortParams,
  () => syncSelectedFromStore(),
  { deep: true }
)

const handleSortChange = (sortParamsToSet: { sort: string }) => {
  props.setSortParams(sortParamsToSet.sort)
}

const onClearAll = async () => {
  if (isBeyondBaseProducts.value) {
    await props.clearAllFacets()
    liveMessage.value = 'Filters cleared'
    window.location.href = URL_CONFIG.PRODUCT_BASE_URL
    return
  }
  await props.clearAllFacets()
  liveMessage.value = 'Filters cleared'
}
</script>
